
import React from 'react';
import { processDynamicFields } from '@/components/cost-center/document-defaults/dynamicFieldProcessor';
import { format } from 'date-fns';

interface EstimateFooterProps {
  estimate: {
    payment_terms?: string;
    special_instructions?: string;
    created_at: string;
    estimate_date?: string;
    expires_at?: string;
    site_street_address?: string;
    site_suburb?: string;
    site_city?: string;
    site_province_state?: string;
    site_postal_code?: string;
    estimate_number?: string;
    validity_period?: number;
  };
  company: {
    name?: string;
    company_phone_number?: string;
    email?: string;
    street_address?: string;
    suburb?: string;
    city?: string;
    province_state?: string;
    postal_code?: string;
    website?: string;
    "Bank Name"?: string;
    "Branch Name"?: string;
    "Branch Code"?: string;
    "Account Number"?: string;
    "Account Holder"?: string;
  };
  userProfile?: {
    first_name?: string;
    last_name?: string;
    personal_phone_number?: string;
    email?: string;
  };
  customer?: {
    name?: string;
    last_name?: string;
    emails?: string[];
    cell_numbers?: string[];
    street_address?: string;
    suburb?: string;
    city?: string;
    province_state?: string;
    postal_code?: string;
  };
  documentDefaults?: {
    estimate_footer?: string;
  };
}

const EstimateFooter: React.FC<EstimateFooterProps> = ({ 
  estimate, 
  company, 
  userProfile,
  customer,
  documentDefaults 
}) => {
  // Calculate validity period in days
  const getValidityDays = () => {
    if (estimate.validity_period) return estimate.validity_period;
    if (!estimate.expires_at) return 14; // default
    const created = new Date(estimate.estimate_date || estimate.created_at);
    const expires = new Date(estimate.expires_at);
    const diffTime = expires.getTime() - created.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 14;
  };

  // Format phone number to xxx xxx xxxx format
  const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return '';
    // Remove any existing spaces or formatting
    const cleaned = phone.replace(/\D/g, '');
    // Format as xxx xxx xxxx
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
    return phone; // Return original if not standard length
  };

  // Format dates consistently
  const formatDate = (dateString: string): string => {
    try {
      return format(new Date(dateString), 'dd MMMM yyyy');
    } catch {
      return dateString;
    }
  };

  // Process custom footer with dynamic fields if available
  const getFooterText = () => {
    console.log('[EstimateFooter] documentDefaults:', documentDefaults);
    console.log('[EstimateFooter] estimate_footer:', documentDefaults?.estimate_footer);
    
    if (documentDefaults?.estimate_footer) {
      const today = format(new Date(), 'dd MMMM yyyy');
      const validityDays = getValidityDays();
      const createdDate = formatDate(estimate.estimate_date || estimate.created_at);
      const expiresDate = estimate.expires_at ? formatDate(estimate.expires_at) : '';
      
      const dynamicData = {
        user: {
          first_name: userProfile?.first_name,
          last_name: userProfile?.last_name,
          email: userProfile?.email,
          personal_phone_number: userProfile?.personal_phone_number,
        },
        company: {
          name: company?.name,
          email: company?.email,
          company_phone_number: company?.company_phone_number,
          street_address: company?.street_address,
          suburb: company?.suburb,
          city: company?.city,
          province_state: company?.province_state,
          postal_code: company?.postal_code,
          website: company?.website,
          bank_name: company?.["Bank Name"],
          branch_name: company?.["Branch Name"],
          branch_code: company?.["Branch Code"],
          account_number: company?.["Account Number"],
          account_holder: company?.["Account Holder"],
        },
        customer: {
          name: [customer?.name, customer?.last_name].filter(Boolean).join(' '),
          first_name: customer?.name,
          last_name: customer?.last_name,
          email: customer?.emails?.[0],
          cell_numbers: customer?.cell_numbers,
          street_address: customer?.street_address,
          suburb: customer?.suburb,
          city: customer?.city,
          province_state: customer?.province_state,
          postal_code: customer?.postal_code,
        },
        estimate: {
          site_street_address: estimate.site_street_address,
          site_suburb: estimate.site_suburb,
          site_city: estimate.site_city,
          site_province_state: estimate.site_province_state,
          site_postal_code: estimate.site_postal_code,
          estimate_number: estimate.estimate_number,
          created_date: (() => { 
            try { 
              const d = estimate.estimate_date || estimate.created_at;
              return d ? format(new Date(d), 'dd MMMM yyyy') : ''; 
            } catch { return ''; } 
          })(),
          expires_date: (() => { 
            try { 
              return estimate.expires_at 
                ? format(new Date(estimate.expires_at), 'dd MMMM yyyy') 
                : ''; 
            } catch { return ''; } 
          })(),
          validity_days: validityDays,
        },
        system: {
          today_date: today,
        }
      };

      console.log('[EstimateFooter] Processing dynamic fields with data:', dynamicData);
      const processedFooter = processDynamicFields(documentDefaults.estimate_footer, dynamicData);
      console.log('[EstimateFooter] Processed footer result:', processedFooter);
      return processedFooter;
    }
    
    // Default footer text
    const validityDays = getValidityDays();
    let defaultFooter = `Please note that the above estimate is valid for a period of ${validityDays} days from the day of issue. If you have any questions please do not hesitate to let us know.`;
    
    if (company.company_phone_number) {
      defaultFooter += ` You can call us on ${formatPhoneNumber(company.company_phone_number)}`;
    }
    
    if (company.email) {
      defaultFooter += ` or email us on ${company.email}`;
    }
    
    if (userProfile?.first_name && userProfile?.personal_phone_number) {
      defaultFooter += ` or contact ${userProfile.first_name} on their cell on ${formatPhoneNumber(userProfile.personal_phone_number)}`;
    }
    
    defaultFooter += '.';
    console.log('[EstimateFooter] Using default footer:', defaultFooter);
    return defaultFooter;
  };

  const footerText = getFooterText();

  return (
    <div className="flex flex-col min-h-0">
      {/* Additional Notes */}
      {(estimate.payment_terms || estimate.special_instructions) && (
        <div className="mb-8">
          <h4 className="font-semibold text-gray-900 mb-3">Important Notes:</h4>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {estimate.payment_terms && (
              <li>{estimate.payment_terms}</li>
            )}
            {estimate.special_instructions && (
              <li>{estimate.special_instructions}</li>
            )}
          </ul>
        </div>
      )}

      {/* Push footer to bottom for screen view */}
      <div className="flex-grow"></div>

      {/* Dynamic footer */}
      <div className="estimate-footer mt-auto pt-6 border-t border-gray-900 text-xs font-bold italic text-gray-900 text-left">
        <p>{footerText}</p>
      </div>
    </div>
  );
};

export default EstimateFooter;
